import { Fragment, type ReactNode } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import {
  PageStatus,
  SkeletonLoader,
  formatAmountWithCommas,
  FormatCurrencyForDisplay,
  Button
} from "@unified-checkout/design-system";
import {
  ArrowUpRightFromSquareRounded,
  CircleExclamation,
  CopyRounded
} from "libs/design-system/src/assets/Icons";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { LINK_STATES } from "@app/dashboard/constants/quick-links";
import { SEARCH_PARAMS_KEYS } from "@app/dashboard/constants/search-params-keys";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { useLangStore } from "@app/dashboard/store/lang";
import { getSingleQuickLink } from "@app/dashboard/services/inner_layout/quick-links";
import { convertCentsToMajorUnit } from "@app/dashboard/utils/convertAmounts";
import { formatToLocalizedDateTime } from "@app/dashboard/utils/dateFormats";
import { CopyToClipboard } from "@app/dashboard/components/inner_layout/CopyToClipboard";
import { InfoCard } from "@app/dashboard/components/inner_layout/Cards/InfoCard";
import { Header } from "./Header";
import styles from "./single-quick-link.module.scss";

export const SingleQuickLink = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const isMultipleFromState = (location.state as { is_multiple?: boolean } | null)?.is_multiple;

  const {
    isLoading,
    isError,
    data: quickLinkDetails,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_QUICK_LINK, id, isMultipleFromState],
    queryFn: async () => {
      const numericId = Number(id);
      try {
        return await getSingleQuickLink(
          numericId,
          isMultipleFromState ? { is_multiple: true } : undefined
        );
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404 && !isMultipleFromState) {
          return getSingleQuickLink(numericId, { is_multiple: true });
        }
        throw err;
      }
    },
    enabled: !!id,
    refetchOnMount: "always",
    select: (response) => response.data,
  });

  const EMPTY_VALUE = '-';

  const detailsBoxRenderer = (
    {
      box = undefined,
      height,
      noOfRows,
      key
    }: {
      box: ReactNode,
      height: string,
      noOfRows: number,
      key: number | string
    }
  ): ReactNode => {
    return (
      isLoading || isFetching ?
        <SkeletonLoader
          type="bordered_box"
          noOfRows={noOfRows}
          height={height}
          key={key}
        />
        :
        box && <Fragment key={key}>{box}</Fragment>
    )
  };

  const getRelatedTransactions = (id: string) => {
    window.open(`${INNER_ROUTES.TRANSACTIONS}?${SEARCH_PARAMS_KEYS.PAYMENT_LINK_ID}=${id}`, '_blank');
  };

  const NavigablePaymentLinkId = quickLinkDetails?.id ?
    <div className={styles["single-quick-link__main__col__navigable-payment-link-id"]}>
      <CopyToClipboard
        textToCopy={String(quickLinkDetails.id)}
        elements={{
          clickToCopy: {
            icon: CopyRounded,
            color: '#144dff',
            fillOrStrokeColor: 'stroke'
          }
        }}
      />
      <Button
        size="md"
        variant="link"
        text={String(quickLinkDetails.id)}
        trailingIcon={ArrowUpRightFromSquareRounded}
        onClickHandler={() => getRelatedTransactions(quickLinkDetails.id)}
      />
      <div className={styles["single-quick-link__main__col__navigable-payment-link-id__view-related-trns"]}>
        {t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.viewRelatedTrnx')}
      </div>
    </div>
    :
    EMPTY_VALUE;

  const allBoxes = [
    detailsBoxRenderer({
      box: quickLinkDetails && <InfoCard
        title={t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.title')}
        noOfColumns={2}
        pairsList={[
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.paymentLinkId'),
            value: NavigablePaymentLinkId
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.orderId'),
            value: quickLinkDetails.order ? String(quickLinkDetails.order) : EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.merchantPaymentLinkId'),
            value: quickLinkDetails.reference_id || EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.currency'),
            value: quickLinkDetails.currency || EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.amount'),
            value: quickLinkDetails.amount_cents && quickLinkDetails.currency
              ? (() => {
                const amount = convertCentsToMajorUnit(quickLinkDetails.amount_cents, quickLinkDetails.currency);
                const formattedAmount = formatAmountWithCommas({ amount, currency: quickLinkDetails.currency });
                return (
                  <>
                    {lang === 'en' && FormatCurrencyForDisplay(quickLinkDetails.currency)}
                    {' '}{formattedAmount}{' '}
                    {lang === 'ar' && FormatCurrencyForDisplay(quickLinkDetails.currency)}
                  </>
                );
              })()
              : EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.status'),
            value: (() => {
              const state = quickLinkDetails.state;
              if (!state) return EMPTY_VALUE;
              switch (state) {
                case LINK_STATES.PAID:
                  return t('innerLayout.quickLinksPage.filters.states.paid');
                case LINK_STATES.UNPAID:
                case 'created':
                  return t('innerLayout.quickLinksPage.filters.states.unpaid');
                case LINK_STATES.CANCELLED:
                  return t('innerLayout.quickLinksPage.filters.states.cancelled');
                case LINK_STATES.DEACTIVATED:
                  return t('innerLayout.quickLinksPage.filters.states.deactivated');
                default:
                  return t('innerLayout.quickLinksPage.filters.states.unpaid');
              }
            })()
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.createdAt'),
            value: quickLinkDetails.created_at
              ? formatToLocalizedDateTime(quickLinkDetails.created_at, lang)
              : EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.expiresAt'),
            value: quickLinkDetails.expires_at
              ? formatToLocalizedDateTime(quickLinkDetails.expires_at, lang)
              : EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.paidAt'),
            value: quickLinkDetails.paid_at
              ? formatToLocalizedDateTime(quickLinkDetails.paid_at, lang)
              : EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.isMultiple'),
            value: quickLinkDetails.is_multiple != null
              ? quickLinkDetails.is_multiple
                ? t('innerLayout.yes')
                : t('innerLayout.no')
              : EMPTY_VALUE
          },
          {
            label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.description'),
            render: () => (
              <p className={styles["single-quick-link__description"]}>
                {quickLinkDetails.description || EMPTY_VALUE}
              </p>
            ),
          },
          ...(quickLinkDetails.is_multiple ? [
            {
              label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.maxAttempts'),
              value: quickLinkDetails.is_unlimited
                ? t('innerLayout.unlimited')
                : (quickLinkDetails.remaining_attempts || quickLinkDetails.remaining_attempts === 0)
                  ? String(quickLinkDetails.remaining_attempts)
                  : t('innerLayout.notSpecified')
            },
            {
              label: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.remainingAttempts'),
              value: quickLinkDetails.is_unlimited
                ? t('innerLayout.unlimited')
                : (quickLinkDetails.max_attempts || quickLinkDetails.max_attempts === 0)
                  ? String(quickLinkDetails.max_attempts)
                  : t('innerLayout.notSpecified')
            }
          ] : [])
        ]}
      />,
      noOfRows: 6,
      height: "20rem",
      key: t('innerLayout.quickLinksPage.singleQuickLink.detailsBox.title')
    })
  ];

  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0);
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0);

  const content = <>
    {
      isMobile ?
        <>
          {col1Boxes}
          {col2Boxes}
        </>
        :
        [col1Boxes, col2Boxes].map((colContent, index) =>
          <div className={styles["single-quick-link__main__col"]} key={`single-quick-link-col-${index}`}>
            {colContent}
          </div>
        )
    }
  </>;

  return (
    <div className={styles["single-quick-link"]}>
      <Header
        id={Number(id)}
        data={quickLinkDetails}
        isLoading={isLoading || isFetching}
        isError={isError}
        refetchData={refetch}
      />

      {isError && (
        <div className={styles["single-quick-link__error-wrapper"]}>
          <PageStatus
            status="error"
            title={t('innerLayout.errors.smthWentWrong')}
            description={t('innerLayout.errors.dataRetrievalErr')}
            icon={CircleExclamation}
          />
        </div>
      )}

      {!isError && (
        <div className={styles["single-quick-link__main"]}>
          {content}
        </div>
      )}
    </div>
  );
};
